import { gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import {
  ImageAlternativeText_GenerateImageAlternativeText,
  ImageAlternativeText_GenerateImageAlternativeTextVariables,
} from './graphql-types/ImageAlternativeText_GenerateImageAlternativeText';

const GENERATE_IMAGE_ALTERNATIVE_TEXT = gql`
  mutation ImageAlternativeText_GenerateImageAlternativeText(
    $input: GenerateImageAlternativeTextInput!
  ) {
    generateImageAlternativeText(input: $input) {
      alternativeText
    }
  }
`;

export const useGenerateImageAltText = () => {
  const toast = useToast();
  const [generateImageAlternativeText, { loading: isGenerating }] = useMutation<
    ImageAlternativeText_GenerateImageAlternativeText,
    ImageAlternativeText_GenerateImageAlternativeTextVariables
  >(GENERATE_IMAGE_ALTERNATIVE_TEXT);

  const generateAlternativeText = React.useCallback(
    async (imageUrl: string): Promise<string | null> => {
      if (!imageUrl) {
        toast({
          title: 'Upload an image before generating alternative text',
          status: 'warning',
        });
        return null;
      }

      try {
        const result = await generateImageAlternativeText({
          variables: {
            input: {
              imageUrl,
            },
          },
        });
        const alternativeText =
          result.data?.generateImageAlternativeText.alternativeText;

        if (!alternativeText) {
          throw new Error(
            'We could not generate alternative text right now. Please try again.'
          );
        }

        return alternativeText;
      } catch (error) {
        toast({
          title:
            error instanceof Error
              ? error.message
              : 'Failed to generate alternative text',
          status: 'error',
        });
        return null;
      }
    },
    [generateImageAlternativeText, toast]
  );

  return { generateAlternativeText, isGenerating };
};
